import React, { useState, useEffect } from 'react';
import { Users, Plus, UserCheck, UserX, Eye, EyeOff, Edit, Key, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

/**
 * A super admin runs the system and a sales agent registers businesses for it -
 * neither belongs to a tenant, so neither gets a Tenant ID. Every other role is
 * a person working in one shop and must have one.
 */
const SYSTEM_ROLES = ['super_admin', 'sales'];
const isSystemRole = (role) => SYSTEM_ROLES.includes(role);

function UserManagement() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isTenantAdmin = user?.role === 'tenant_admin';
  const canManageUsers = isSuperAdmin || isTenantAdmin;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPasswordResetForm, setShowPasswordResetForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    tenantId: '',
    tenantName: '',
    address: '',
    phoneNumber: ''
  });
  const [passwordResetData, setPasswordResetData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (canManageUsers) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [user, canManageUsers]);

  // Only system owners and tenant admins can access this page
  if (!canManageUsers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/auth/users');
      setUsers(response.data.users || []);
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username || !formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!formData.address || !formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    }
    
    // Super admins choose the tenant. Tenant admins are scoped by the server to
    // their own business, so asking them for a tenant id would be redundant.
    if (isSuperAdmin && !isSystemRole(formData.role)) {
      if (!formData.tenantId || !formData.tenantId.trim()) {
        errors.tenantId = 'Tenant ID is required for tenant users';
      }
      if (!formData.tenantName || !formData.tenantName.trim()) {
        errors.tenantName = 'Tenant name is required for tenant users';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        address: formData.address,
        phoneNumber: formData.phoneNumber
      };

      userData.role = isTenantAdmin ? 'user' : formData.role;

      // Tenant admins are pinned to their own business by the server. Super
      // admins still choose a tenant explicitly when creating tenant users.
      if (isSuperAdmin && !isSystemRole(formData.role)) {
        userData.tenantId = formData.tenantId;
        userData.tenantName = formData.tenantName;
      }

      await api.post('/api/auth/register', userData);
      
      setSuccess('User created successfully!');
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'user',
        tenantId: '',
        tenantName: '',
        address: '',
        phoneNumber: ''
      });
      setShowCreateForm(false);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/api/auth/users/${userId}/status`, {
        isActive: !currentStatus
      });
      
      setSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userData) => {
    if (!window.confirm(`Are you sure you want to delete user "${userData.username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/api/auth/users/${userData._id}`);
      setSuccess(`User "${userData.username}" deleted successfully!`);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleEditUser = (userData) => {
    setEditingUser(userData);
    setFormData({
      username: userData.username || '',
      email: userData.email || '',
      password: '', // Don't populate password for security
      role: isTenantAdmin ? 'user' : userData.role || '',
      tenantId: userData.tenantId || '',
      tenantName: userData.tenantName || '',
      address: userData.address || '',
      phoneNumber: userData.phoneNumber || ''
    });
    setShowEditForm(true);
    setFormErrors({});
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!formData.username || !formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    // Password is optional for edit
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters if provided';
    }

    if (!formData.address || !formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    }
    
    // Super admins choose the tenant. Tenant admins are scoped by the server to
    // their own business, so asking them for a tenant id would be redundant.
    if (isSuperAdmin && !isSystemRole(formData.role)) {
      if (!formData.tenantId || !formData.tenantId.trim()) {
        errors.tenantId = 'Tenant ID is required for tenant users';
      }
      if (!formData.tenantName || !formData.tenantName.trim()) {
        errors.tenantName = 'Tenant name is required for tenant users';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const userData = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        address: formData.address,
        phoneNumber: formData.phoneNumber
      };

      // Only include password if provided
      if (formData.password && formData.password.trim()) {
        userData.password = formData.password;
      }

      userData.role = isTenantAdmin ? 'user' : formData.role;

      // Tenant admins cannot move a staff login between businesses; the server
      // keeps the existing tenant on edit.
      if (isSuperAdmin && !isSystemRole(formData.role)) {
        userData.tenantId = formData.tenantId;
        userData.tenantName = formData.tenantName;
      }

      await api.put(`/api/auth/users/${editingUser._id}`, userData);
      
      setSuccess('User updated successfully!');
      setShowEditForm(false);
      setEditingUser(null);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handlePasswordReset = (userData) => {
    setEditingUser(userData);
    setPasswordResetData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordResetForm(true);
    setFormErrors({});
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    
    if (!passwordResetData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordResetData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (passwordResetData.newPassword !== passwordResetData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      await api.patch(`/api/auth/users/${editingUser._id}/reset-password`, {
        newPassword: passwordResetData.newPassword
      });
      
      setSuccess('Password reset successfully!');
      setShowPasswordResetForm(false);
      setEditingUser(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handlePasswordResetInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordResetData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cafe-600"></div>
      </div>
    );
  }

  // Layout's main already supplies max-w-7xl and the horizontal padding -
  // repeating them here indented the page twice and cost width on a tablet
  return (
    <div className="py-2">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center min-w-0">
            <Users className="h-7 w-7 sm:h-8 sm:w-8 text-cafe-600 mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {isTenantAdmin ? 'Staff Access' : 'User Management'}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                {isTenantAdmin
                  ? 'Create and manage staff logins for your business'
                  : 'Manage users and their access permissions'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center justify-center flex-shrink-0 px-4 py-2 min-h-[44px] whitespace-nowrap bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2 flex-shrink-0" />
            {isTenantAdmin ? 'Create Staff' : 'Create User'}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Edit User Form */}
      {showEditForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
            <button
              onClick={() => {
                setShowEditForm(false);
                setEditingUser(null);
                setFormErrors({});
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleEditSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.username ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter username"
              />
              {formErrors.username && (
                <p className="text-red-500 text-sm mt-1">{formErrors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter email"
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password (leave blank to keep current)
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                    formErrors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter new password (optional)"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
              )}
            </div>

            {isSuperAdmin ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                >
                  <option value="user">User</option>
                  <option value="tenant_admin">Tenant Admin</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="sales">Sales Agent</option>
                </select>
                {formData.role === 'sales' && (
                  <p className="text-gray-500 text-xs mt-1">
                    Registers businesses in the field. They see only the ones they
                    registered, and no shop's sales figures - so they need no tenant.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="px-3 py-2 min-h-[42px] bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                  Staff
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Staff can use the sales screens for {user?.tenantName || 'your business'}.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter phone number"
              />
              {formErrors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1">{formErrors.phoneNumber}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.address ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full address"
              />
              {formErrors.address && (
                <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>
              )}
            </div>

            {isSuperAdmin && !isSystemRole(formData.role) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant ID *
                  </label>
                  <input
                    type="text"
                    name="tenantId"
                    value={formData.tenantId}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                      formErrors.tenantId ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter tenant ID"
                  />
                  {formErrors.tenantId && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.tenantId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant Name *
                  </label>
                  <input
                    type="text"
                    name="tenantName"
                    value={formData.tenantName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                      formErrors.tenantName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter tenant/business name"
                  />
                  {formErrors.tenantName && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.tenantName}</p>
                  )}
                </div>
              </>
            )}

            <div className="md:col-span-2 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingUser(null);
                  setFormErrors({});
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors"
              >
                Update User
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Password Reset Form */}
      {showPasswordResetForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Reset Password for {editingUser?.username}
            </h2>
            <button
              onClick={() => {
                setShowPasswordResetForm(false);
                setEditingUser(null);
                setFormErrors({});
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handlePasswordResetSubmit} className="max-w-md">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password *
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordResetData.newPassword}
                onChange={handlePasswordResetInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.newPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter new password"
              />
              {formErrors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{formErrors.newPassword}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordResetData.confirmPassword}
                onChange={handlePasswordResetInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Confirm new password"
              />
              {formErrors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{formErrors.confirmPassword}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordResetForm(false);
                  setEditingUser(null);
                  setFormErrors({});
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      )}
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <div className="mb-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {isTenantAdmin ? 'Create Staff Login' : 'Create New User'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.username ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter username"
              />
              {formErrors.username && (
                <p className="text-red-500 text-sm mt-1">{formErrors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter email"
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                    formErrors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
              )}
            </div>

            {isSuperAdmin ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                >
                  <option value="user">User</option>
                  <option value="tenant_admin">Tenant Admin</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="sales">Sales Agent</option>
                </select>
                {formData.role === 'sales' && (
                  <p className="text-gray-500 text-xs mt-1">
                    Registers businesses in the field. They see only the ones they
                    registered, and no shop's sales figures - so they need no tenant.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="px-3 py-2 min-h-[42px] bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                  Staff
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Staff can use the sales screens for {user?.tenantName || 'your business'}.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter phone number"
              />
              {formErrors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1">{formErrors.phoneNumber}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                  formErrors.address ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full address"
              />
              {formErrors.address && (
                <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>
              )}
            </div>

            {isSuperAdmin && !isSystemRole(formData.role) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant ID *
                  </label>
                  <input
                    type="text"
                    name="tenantId"
                    value={formData.tenantId}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                      formErrors.tenantId ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter tenant ID"
                  />
                  {formErrors.tenantId && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.tenantId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant Name *
                  </label>
                  <input
                    type="text"
                    name="tenantName"
                    value={formData.tenantName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
                      formErrors.tenantName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter tenant/business name"
                  />
                  {formErrors.tenantName && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.tenantName}</p>
                  )}
                </div>
              </>
            )}

            <div className="md:col-span-2 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors"
              >
                {isTenantAdmin ? 'Create Staff' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isTenantAdmin ? 'Business Users' : 'All Users'}
          </h2>
        </div>
        
        {users.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No users found. Create your first user to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((userData) => (
                  <tr key={userData._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {userData.username}
                        </div>
                        <div className="text-sm text-gray-500">
                          {userData.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {userData.phoneNumber}
                      </div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {userData.address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        userData.role === 'super_admin'
                          ? 'bg-purple-100 text-purple-800'
                          : userData.role === 'tenant_admin'
                          ? 'bg-blue-100 text-blue-800'
                          : userData.role === 'sales'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {userData.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {userData.tenantName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {userData.tenantId || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        userData.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {userData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditUser(userData)}
                          disabled={isTenantAdmin && userData.role !== 'user'}
                          className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-40 disabled:hover:bg-blue-100 transition-colors"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handlePasswordReset(userData)}
                          disabled={isTenantAdmin && userData.role !== 'user'}
                          className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 disabled:opacity-40 disabled:hover:bg-orange-100 transition-colors"
                        >
                          <Key className="h-4 w-4 mr-1" />
                          Reset
                        </button>
                        <button
                          onClick={() => toggleUserStatus(userData._id, userData.isActive)}
                          disabled={userData._id === user?.id || (isTenantAdmin && userData.role !== 'user')}
                          className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                            userData.isActive
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-green-700 bg-green-100 hover:bg-green-200'
                          } disabled:opacity-40 disabled:hover:bg-opacity-100 transition-colors`}
                        >
                          {userData.isActive ? (
                            <>
                              <UserX className="h-4 w-4 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-1" />
                              Activate
                            </>
                          )}
                        </button>
                        {(isSuperAdmin ? userData.role !== 'super_admin' : userData.role === 'user') && (
                          <button
                            onClick={() => handleDeleteUser(userData)}
                            disabled={userData._id === user?.id}
                            className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 disabled:opacity-40 disabled:hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
